import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import axios from 'axios';

const RugbyLiveScore = () => {
  const [activeTab, setActiveTab] = useState('live');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [liveMatches, setLiveMatches] = useState([]);
  const [scheduledMatches, setScheduledMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

  const tabs = [
    { id: 'all', label: 'All' },
    { id: 'live', label: 'Live', count: liveMatches.length },
    { id: 'finished', label: 'Finished' },
    { id: 'upcoming', label: 'Upcoming' }
  ];

  const fetchLiveMatches = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/scores/live/rugby`);
      setLiveMatches(response.data.events || []);
    } catch (error) {
      console.error('Error fetching live matches:', error);
    }
  };

  const fetchScheduledMatches = async (date) => {
    try {
      const formattedDate = date.toISOString().slice(0, 10);
      const response = await axios.get(`${BACKEND_URL}/api/scores/scheduled/rugby/${formattedDate}`);
      setScheduledMatches(response.data.events || []);
    } catch (error) {
      console.error('Error fetching scheduled matches:', error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([
        fetchLiveMatches(),
        fetchScheduledMatches(selectedDate)
      ]);
      setLoading(false);
    };

    fetchData();
    
    // Refresh live data every 30 seconds
    const interval = setInterval(() => fetchLiveMatches(), 30000);
    return () => clearInterval(interval);
  }, [selectedDate]);

  const getCountryFlag = (country) => {
    if (!country?.alpha2) return null;
    return `https://flagcdn.com/w20/${country.alpha2.toLowerCase()}.png`;
  };

  const getTeamFlag = (team) => {
    if (team.national) {
      return getCountryFlag(team.country);
    }
    // For franchise teams, you might want to use team logos
    return `https://api.sofascore.app/api/v1/team/${team.id}/image`;
  };

  const getTournamentLogo = (tournament) => {
    if (tournament?.uniqueTournament?.id) {
      return `https://api.sofascore.app/api/v1/unique-tournament/${tournament.uniqueTournament.id}/image`;
    }
    return '';
  };

  const formatScore = (score) => {
    if (!score) return '';
    return `${score.current}`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp * 1000).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDateChange = (increment) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(selectedDate.getDate() + increment);
    setSelectedDate(newDate);
  };

  const getDisplayMatches = () => {
    switch (activeTab) {
      case 'live':
        return liveMatches;
      case 'finished':
        return scheduledMatches.filter(match => match.status.type === 'finished');
      case 'upcoming':
        return scheduledMatches.filter(match => match.status.type === 'notstarted');
      default:
        return [...liveMatches, ...scheduledMatches];
    }
  };

  return (
    <div className="w-full p-4">
      {/* Date Navigation */}
      <div className="flex items-center justify-between mb-4 text-white">
        <div className="flex items-center space-x-2">
          <button 
            onClick={() => handleDateChange(-1)}
            className="p-1.5 text-black hover:text-white hover:bg-gray-700 rounded-full"
          >
            <ChevronLeft size={20} />
          </button>
          <button className="px-3 py-1.5  bg-gray-800 rounded-lg flex items-center space-x-2">
            <Calendar size={16} />
            <span>
              {selectedDate.toLocaleDateString('en-GB', { 
                day: '2-digit',
                month: '2-digit'
              })}
            </span>
          </button>
          <button 
            onClick={() => handleDateChange(1)}
            className="p-1.5 text-black hover:bg-gray-700 hover:text-white rounded-full"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-2 mb-4">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-3 py-1.5 rounded-full text-sm ${
              activeTab === tab.id ? 'bg-red-500' : 'hover:bg-gray-700 hover:text-white'
            }`}
          >
            {tab.label} {tab.count && `(${tab.count})`}
          </button>
        ))}
      </div>

      {/* Matches List */}
      {loading ? (
        <div className="text-center py-4">Loading...</div>
      ) : (
        <div className="space-y-4">
          {getDisplayMatches().map((match) => (
            <a href={`https://www.sofascore.com/rugby/match/${match.slug}/${match.customId}#id:${match.id}`} target='_blank' >
            <div key={match.id} className="rounded-2xl border border-gray-200 p-3 mb-5" >
              {/* Tournament Header */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                {getTournamentLogo(match.tournament) ? (
                    <img
                      src={getTournamentLogo(match.tournament)}
                      alt={match.tournament.name}
                      className="w-5 h-5 object-contain"
                      onError={(e) => {
                        console.log(e);
                      }}
                    />
                  ) : (
                    <img
                      src={getCountryFlag(match.tournament.category.country)}
                      alt={match.tournament.category.country?.name}
                      className="w-5 h-5 rounded-full"
                    />
                  )}
                  <span className="text-sm">{match.tournament.name}</span>
                </div>
              </div>

              {/* Teams */}
              {match.status.type === 'notstarted' ? (
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-2">
                      <img
                        src={getTeamFlag(match.homeTeam)}
                        alt={match.homeTeam.name}
                        className="w-4 h-4 object-contain"
                      />
                      <span className="font-medium">{match.homeTeam.shortName}</span>
                    </div>
                    <span className="text-gray-400">vs</span>
                    <div className="flex items-center space-x-2">
                      <img
                        src={getTeamFlag(match.awayTeam)}
                        alt={match.awayTeam.name}
                        className="w-4 h-4 object-contain"
                      />
                      <span className="font-medium">{match.awayTeam.shortName}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <img
                        src={getTeamFlag(match.homeTeam)}
                        alt={match.homeTeam.name}
                        className="w-4 h-4 object-contain"
                      />
                      <span className="font-medium">{match.homeTeam.shortName}</span>
                    </div>
                    <span className="text-right">{formatScore(match.homeScore)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <img
                        src={getTeamFlag(match.awayTeam)}
                        alt={match.awayTeam.name}
                        className="w-4 h-4 object-contain"
                      />
                      <span className="font-medium">{match.awayTeam.shortName}</span>
                    </div>
                    <span className="text-right">{formatScore(match.awayScore)}</span>
                  </div>
                </div>
              )}

              {/* Status */}
              <div className="mt-2">
                <span className={`text-xs ${
                  match.status.type === 'inprogress' ? 'text-red-400' : 'text-gray-400'
                }`}>
                  {formatDate(match.status.type === 'finished' ? match.changes.changeTimestamp : match.status.type === 'notstarted' ? match.startTimestamp : null)}
                </span>
              </div>
            </div>
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

export default RugbyLiveScore;